import { H3TileLayer, QuadbinTileLayer, VectorTileLayer, RasterTileLayer } from '@deck.gl/carto'
import {
  QueryParameters,
  h3QuerySource,
  h3TableSource,
  quadbinQuerySource,
  quadbinTableSource,
  rasterSource,
  vectorQuerySource,
  vectorTableSource,
  vectorTilesetSource,
  VectorTilesetSourceOptions,
  H3TableSourceOptions,
  QuadbinTableSourceOptions,
  VectorTableSourceOptions,
  H3QuerySourceOptions,
  QuadbinQuerySourceOptions,
  VectorQuerySourceOptions,
  TableSourceOptions,
  TilejsonResult,
  TileResolution,
  QuerySourceOptions,
  SourceOptionalOptions,
  SourceRequiredOptions
} from '@carto/api-client'
import cartoApiClientWorkerUrl from '@carto/api-client/worker-compat?url'
import { SourceFilters } from '@carto/react-redux'
import { MapType } from 'features/maps/types'
import { getColumnNameFromGeoColumn, getSpatialIndexFromGeoColumn } from 'features/builder/state/builderSliceUtils'
import { SpatialIndex } from 'features/builder/types'
import {
  DEFAULT_AGGREGATION_EXP,
  DEFAULT_AGGREGATION_RES_LEVEL_H3,
  DEFAULT_AGGREGATION_RES_LEVEL_QUADBIN,
  DEFAULT_TILE_DEBOUNCE_MS,
  DEFAULT_TILE_RESOLUTION,
  DEFAULT_TILE_SIZE,
  REDUCED_QUERIES_H3_TILE_SIZE,
  REDUCED_QUERIES_TILE_DEBOUNCE_MS,
  REDUCED_QUERIES_TILE_RESOLUTION,
  REDUCED_QUERIES_TILE_SIZE
} from 'features/builder/ui/builderConstants'
import { getFlag } from 'features/account/state/accountSlice'
import { getStore } from 'app/state/storeRegistry'
import { FeatureFlag } from 'app/types'
import {
  cartoRasterTileLoadOptions,
  cartoSpatialTileLoadOptions,
  cartoVectorTileLoadOptions,
  fetchWorkerScriptAsBlobUrl
} from './deckGlWorkers'
import { isInSandboxedIframe } from 'app/utils/embedding'
import { AuditTags, buildAuditTags } from './buildAuditTags'

export interface CartoSourceProps {
  accessToken: string
  apiBaseUrl: string
  connection: string
  headers?: Record<string, string>
  geoColumn?: string | null
  columns?: string[] | null
  type: MapType
  source: string
  queryParameters?: QueryParameters | null
  aggregationExp?: string | null
  aggregationResLevel?: number | null
  filters?: SourceFilters
  localCache?: { cacheControl: 'no-cache'[] }
  auditTags?: AuditTags
}

export function getSourceOptions({
  accessToken,
  apiBaseUrl,
  connection,
  headers,
  auditTags
}: Pick<CartoSourceProps, 'auditTags' | 'accessToken' | 'apiBaseUrl' | 'connection' | 'headers' | 'localCache'>) {
  return {
    accessToken,
    connectionName: connection,
    apiBaseUrl,
    tags: buildAuditTags(auditTags ?? {}),
    headers: headers || {},
    ...(headers?.['Cache-Control']?.includes('no-cache') && {
      localCache: {
        cacheControl: ['no-cache'] as const
      }
    })
  } satisfies Omit<SourceOptionalOptions & SourceRequiredOptions, 'clientId'>
}

export async function getCartoSource({
  geoColumn,
  columns,
  type,
  source,
  queryParameters,
  aggregationExp,
  aggregationResLevel,
  filters,
  ...options
}: CartoSourceProps): Promise<TilejsonResult> {
  const sourceOptions = getSourceOptions(options)
  const spatialDataColumn = getColumnNameFromGeoColumn(geoColumn) || undefined
  const spatialIndex = geoColumn ? getSpatialIndexFromGeoColumn(geoColumn) : undefined
  const tileResolution = getDynamicTileResolution(spatialIndex)
  const isH3 = spatialIndex === SpatialIndex.H3
  const isQuadbin = spatialIndex === SpatialIndex.QUADBIN

  if (typeof aggregationResLevel !== 'number' && isH3) {
    aggregationResLevel = DEFAULT_AGGREGATION_RES_LEVEL_H3
  } else if (typeof aggregationResLevel !== 'number' && isQuadbin) {
    aggregationResLevel = DEFAULT_AGGREGATION_RES_LEVEL_QUADBIN
  }

  const spatialIndexOptions = {
    aggregationExp: !aggregationExp ? DEFAULT_AGGREGATION_EXP : aggregationExp,
    aggregationResLevel: scaleAggregationResLevel(aggregationResLevel as number, tileResolution),
    spatialDataColumn,
    ...(filters && { filters })
  } as H3QuerySourceOptions | H3QuerySourceOptions
  const tilesetOptions = {
    ...sourceOptions,
    tableName: source
  } as VectorTilesetSourceOptions
  const tableOptions = {
    ...sourceOptions,
    tableName: source,
    tileResolution
  } as TableSourceOptions
  const queryOptions = {
    ...sourceOptions,
    sqlQuery: source,
    tileResolution,
    ...(queryParameters && { queryParameters })
  } as QuerySourceOptions
  const vectorOptions = {
    spatialDataColumn,
    ...(columns && { columns }),
    ...(filters && { filters }),
    ...(aggregationExp && { aggregationExp })
  } as VectorTableSourceOptions | VectorQuerySourceOptions

  if (type === MapType.raster) {
    return rasterSource({ ...sourceOptions, tableName: source })
  }
  if (type === MapType.tileset) {
    // TODO: Ideally we should split into h3TilesetSource and quadbinTilesetSource, but locally don't
    // have data to choose. In the meantime, vectorTilesetSource works.
    return vectorTilesetSource({
      ...(tilesetOptions as VectorTilesetSourceOptions),
      ...(isInSandboxedIframe() && {
        widgetWorkerUrl: await getCartoApiClientWorkerBlobUrl()
      })
    })
  }

  if (type === MapType.table) {
    if (isH3) {
      return h3TableSource({
        ...(tableOptions as H3TableSourceOptions),
        ...spatialIndexOptions
      })
    } else if (isQuadbin) {
      return quadbinTableSource({
        ...(tableOptions as QuadbinTableSourceOptions),
        ...spatialIndexOptions
      })
    } else {
      return vectorTableSource({
        ...(tableOptions as VectorTableSourceOptions),
        ...vectorOptions
      })
    }
  }

  if (type === MapType.query) {
    if (isH3) {
      return h3QuerySource({
        ...(queryOptions as H3QuerySourceOptions),
        ...spatialIndexOptions
      })
    } else if (isQuadbin) {
      return quadbinQuerySource({
        ...(queryOptions as QuadbinQuerySourceOptions),
        ...spatialIndexOptions
      })
    } else {
      return vectorQuerySource({
        ...(queryOptions as VectorQuerySourceOptions),
        ...vectorOptions
      })
    }
  }
  throw new Error(`Invalid source type: ${type}`)
}

export function getCartoLayerInstance({
  type,
  geoColumn,
  scheme
}: {
  type: MapType
  geoColumn: string | undefined
  scheme: string
}) {
  if (type === MapType.raster) {
    return { layerClass: RasterTileLayer, props: { loadOptions: cartoRasterTileLoadOptions } }
  }

  const spatialIndex = geoColumn && getSpatialIndexFromGeoColumn(geoColumn)

  if (spatialIndex === SpatialIndex.QUADBIN || scheme === SpatialIndex.QUADBIN) {
    return { layerClass: QuadbinTileLayer, props: { loadOptions: cartoSpatialTileLoadOptions } }
  }

  if (spatialIndex === SpatialIndex.H3 || scheme === SpatialIndex.H3) {
    return { layerClass: H3TileLayer, props: { loadOptions: cartoSpatialTileLoadOptions } }
  }
  return { layerClass: VectorTileLayer, props: { loadOptions: cartoVectorTileLoadOptions } }
}

/**
 * State of `aggregationResLevel` in the UI and backend config is based on an assumption of
 * 512x512px tiles. Because we may change tile resolution for performance goals, the
 * `aggregationResLevel` passed to the deck.gl layer must be scaled with tile resolution.
 */
function scaleAggregationResLevel(aggregationResLevel: number, tileResolution: number): number | undefined {
  if (typeof aggregationResLevel !== 'number') return
  return aggregationResLevel - Math.log2(0.5 / tileResolution)
}

/**
 * Returns default tile resolution for dynamic tilesets, based on layer configuration
 * and feature flags. Result is not applicable for static tilesets, for which tile
 * resolution is defined by tilejson.
 */
export function getDynamicTileResolution(spatialIndex?: SpatialIndex | null): TileResolution {
  const isReducedQueries = getFlag(getStore().getState(), FeatureFlag.ReduceNumberOfQueries)

  // TODO: Support increased tile size and resolution for dynamic H3 spatial indexes.
  if (isReducedQueries && spatialIndex !== SpatialIndex.H3) {
    return REDUCED_QUERIES_TILE_RESOLUTION
  }

  return DEFAULT_TILE_RESOLUTION
}

export function getTileSize({
  tileResolution,
  scheme,
  sourceType,
  spatialIndex
}: {
  tileResolution: number | undefined
  scheme: string
  sourceType: MapType
  spatialIndex: SpatialIndex | null
}) {
  if (!getFlag(getStore().getState(), FeatureFlag.ReduceNumberOfQueries)) {
    return DEFAULT_TILE_SIZE
  }
  if (sourceType === MapType.raster) {
    return DEFAULT_TILE_SIZE
  }

  const isH3 = spatialIndex === SpatialIndex.H3 || scheme === SpatialIndex.H3

  // If unspecified use 0.5 for tilesets and H3, or 1.0 for anything else.
  // TODO: Remove tileset special case after backend includes 'tileResolution' unconditionally.
  // TODO: Support increased tile size and resolution for dynamic H3 spatial indexes.
  if (sourceType === MapType.tileset || isH3) {
    tileResolution ||= DEFAULT_TILE_RESOLUTION
  } else {
    tileResolution ||= REDUCED_QUERIES_TILE_RESOLUTION
  }

  // For H3 spatial indexes, we support tileResolutions 0.5 and 1 for
  // static tilesets, and only 1 for dynamic tilesets.
  // TODO: Support increased tile size and resolution for dynamic H3 spatial indexes.
  if (isH3) {
    if (tileResolution === REDUCED_QUERIES_TILE_RESOLUTION) {
      return REDUCED_QUERIES_H3_TILE_SIZE
    }
    return DEFAULT_TILE_SIZE
  }

  return tileResolution * REDUCED_QUERIES_TILE_SIZE
}

export function getDebounceTime() {
  if (getFlag(getStore().getState(), FeatureFlag.ReduceNumberOfQueries)) {
    return REDUCED_QUERIES_TILE_DEBOUNCE_MS
  }
  return DEFAULT_TILE_DEBOUNCE_MS
}

let cartoApiClientWorkerBlobUrl: string | undefined

async function getCartoApiClientWorkerBlobUrl() {
  if (!cartoApiClientWorkerBlobUrl) {
    const actualResolvedUrl = new URL(cartoApiClientWorkerUrl, import.meta.url)
    cartoApiClientWorkerBlobUrl = await fetchWorkerScriptAsBlobUrl(actualResolvedUrl)
  }
  return cartoApiClientWorkerBlobUrl
}
